import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { GenericModal, Skeleton, cn } from '@org/ui';
import { formatDateTime } from './format';

type ChatTranscriptModalProps = {
  sessionId: string;
  onClose: () => void;
};

/** Reads one AI-tutor conversation, student turns on the right. */
export function ChatTranscriptModal({ sessionId, onClose }: ChatTranscriptModalProps) {
  const { t, i18n } = useTranslation('admin');
  const { data, isLoading, error } = trpc.analytics.chat.transcript.useQuery({ sessionId });

  return (
    <GenericModal
      show
      onClose={onClose}
      size="lg"
      centered
      title={
        data
          ? `${data.name} · ${formatDateTime(data.createdAt, i18n.language)}`
          : t('analytics.chat.transcript.title')
      }
      body={
        isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-2/3" />
            <Skeleton className="h-10 w-1/2 self-end" />
            <Skeleton className="h-10 w-3/4" />
          </div>
        ) : error || !data ? (
          <p className="py-6 text-center text-sm text-destructive">{t('analytics.common.error')}</p>
        ) : data.messages.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('analytics.chat.transcript.empty')}
          </p>
        ) : (
          <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
            {data.messages.map((m) => (
              <div key={m.messageId} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap',
                    m.role === 'user' ? 'bg-[#cf2e2e]/10 text-gray-900' : 'bg-muted text-gray-900',
                  )}
                >
                  <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                    {m.role === 'user' ? t('analytics.chat.transcript.student') : t('analytics.chat.transcript.tutor')}
                    {' · '}
                    {formatDateTime(m.createdAt, i18n.language)}
                  </div>
                  {m.content}
                </div>
              </div>
            ))}
          </div>
        )
      }
    />
  );
}
